import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import MatrixRain from "@/components/MatrixRain";
import { PageHeader, Pill } from "@/components/perf";
import { Brain, Zap, Flame, Target, Award, Loader2, ChevronRight, Lock, Sparkles, Swords, FileText, History, Dumbbell, ArrowLeft } from "lucide-react";

const DIM_LABELS = {
  tactical_understanding: "Tactical Understanding", shot_selection: "Shot Selection",
  risk_management: "Risk Management", positioning: "Positioning", opponent_reading: "Opponent Reading",
  partner_awareness: "Partner Awareness", court_geometry: "Court Geometry",
  match_context: "Match Context", adaptability: "Adaptability", second_order: "Second-Order Thinking",
};
const bandColor = (s) => (s >= 90 ? "#22D3EE" : s >= 75 ? "#A3E635" : s >= 60 ? "#FACC15" : s >= 40 ? "#FB923C" : "#F87171");

const IQBar = ({ label, value, testid }) => (
  <div data-testid={testid}>
    <div className="flex justify-between text-[10px] uppercase tracking-[0.15em] mb-1">
      <span className="text-zinc-400">{label}</span>
      <span className="font-bold" style={{ color: value == null ? "#52525B" : bandColor(value) }}>{value == null ? "—" : value}</span>
    </div>
    <div className="h-1.5 bg-pb-elevated border border-pb-border overflow-hidden">
      <div className="h-full transition-all duration-700" style={{ width: `${value || 0}%`, background: value == null ? "transparent" : bandColor(value) }} />
    </div>
  </div>
);

function QuestionCard({ q, onDone, onExit }) {
  const [choice, setChoice] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = q.mode === "choice" ? { sid: q.sid, choice } : { sid: q.sid, text };
      const { data } = await api.post("/brain/answer", payload);
      setResult(data);
      if (data.new_badges?.length) data.new_badges.forEach((b) => toast.success(`Badge unlocked: ${b.name}`));
      onDone(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (result) {
    return (
      <div data-testid="brain-result" className="bg-pb-surface border border-pb-border p-6 animate-fade-up">
        <div className="flex flex-wrap items-center gap-4 mb-5">
          <div className="font-display font-black text-6xl" style={{ color: bandColor(result.score) }}>{Math.round(result.score)}</div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Decision IQ · This Answer</div>
            <div className="font-display font-black uppercase text-xl" style={{ color: bandColor(result.score) }}>{result.band}</div>
            {result.correct != null && <Pill color={result.correct ? "#A3E635" : "#F87171"}>{result.correct ? "Correct read" : "Wrong option"}</Pill>}
          </div>
          <div className="ml-auto text-right">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">XP Earned</div>
            <div className="font-display font-black text-2xl text-amber-400">+{result.xp_earned}{result.was_daily && <span className="text-[10px] text-zinc-500 ml-1">DAILY 2×</span>}</div>
          </div>
        </div>
        <div className="border border-cyan-400/30 bg-cyan-400/5 p-4 mb-4">
          <div className="text-[10px] uppercase tracking-[0.25em] text-cyan-300 mb-1.5 flex items-center gap-1.5"><Brain className="w-3.5 h-3.5" /> AI Coach Analysis</div>
          <p className="text-sm text-zinc-200 leading-relaxed" data-testid="brain-explanation">{result.explanation}</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div className="border border-pb-border p-4">
            <div className="text-[10px] uppercase tracking-[0.25em] text-lime-400 mb-1.5">Correct Solution</div>
            <p className="text-sm text-zinc-300">{result.correct_solution}</p>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mt-3 mb-1">Why</div>
            <p className="text-xs text-zinc-400">{result.why}</p>
          </div>
          <div className="border border-pb-border p-4 space-y-2">
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">10-Dimension Breakdown</div>
            {Object.entries(result.dims || {}).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2">
                <div className="text-[9px] uppercase tracking-wider text-zinc-500 w-36 shrink-0">{DIM_LABELS[k]}</div>
                <div className="flex-1 h-1 bg-pb-elevated"><div className="h-full" style={{ width: `${v}%`, background: bandColor(v) }} /></div>
                <div className="text-[9px] font-bold w-6 text-right" style={{ color: bandColor(v) }}>{Math.round(v)}</div>
              </div>
            ))}
          </div>
        </div>
        {result.recommended_drill && (
          <div className="border border-amber-400/40 bg-amber-400/5 p-4 mb-4 flex flex-wrap items-center gap-3" data-testid="brain-drill-rec">
            <Dumbbell className="w-5 h-5 text-amber-400 shrink-0" />
            <div className="flex-1 min-w-[200px]">
              <div className="text-[10px] uppercase tracking-[0.25em] text-amber-400">Recommended Drill · Weakness: {result.weakness}</div>
              <div className="font-display font-black uppercase">{result.recommended_drill.name} <span className="text-zinc-500 text-xs">· {result.recommended_drill.level}</span></div>
              <p className="text-xs text-zinc-400 mt-0.5">{result.recommended_drill.purpose}</p>
            </div>
            <button data-testid="start-drill-btn" onClick={() => window.location.assign("/training")}
              className="border border-amber-400 text-amber-400 hover:bg-amber-400 hover:text-black px-4 py-2 text-[10px] font-black uppercase tracking-widest transition-colors">Start Drill</button>
          </div>
        )}
        <div className="flex flex-wrap gap-3">
          {result.quota?.remaining > 0 ? (
            <button data-testid="next-scenario-btn" onClick={onExit.next}
              className="flex items-center gap-2 bg-cyan-400 text-black px-5 py-2.5 text-[11px] font-black uppercase tracking-widest hover:bg-cyan-300 transition-colors">Next Scenario <ChevronRight className="w-4 h-4" /></button>
          ) : (
            <button onClick={() => window.location.assign("/upgrade")}
              className="flex items-center gap-2 bg-amber-400 text-black px-5 py-2.5 text-[11px] font-black uppercase tracking-widest">Quota reached — Upgrade for more</button>
          )}
          <button data-testid="back-dashboard-btn" onClick={onExit.dash}
            className="border border-pb-border text-zinc-400 px-5 py-2.5 text-[11px] font-black uppercase tracking-widest hover:border-cyan-400 hover:text-cyan-300 transition-colors">My Brain</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="brain-question" className="bg-pb-surface border border-cyan-400/30 p-6 animate-fade-up">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Pill color="#22D3EE">Level {q.level} · {q.level_name}</Pill>
        {q.categories.map((c) => <Pill key={c} color="#A3E635">{c}</Pill>)}
        <span className="ml-auto text-[9px] uppercase tracking-[0.25em] text-zinc-600">{q.mode === "choice" ? "Multiple Choice" : "Written Decision"}</span>
      </div>
      <div className="font-display font-black uppercase text-2xl mb-3 text-cyan-300">{q.title}</div>
      <div className="border-l-2 border-cyan-400/50 pl-4 mb-4">
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Scenario</div>
        <p className="text-sm text-zinc-200 leading-relaxed">{q.scenario}</p>
      </div>
      <p className="font-bold text-base mb-4">{q.question}</p>
      {q.mode === "choice" ? (
        <div className="space-y-2 mb-5">
          {q.options.map((o, i) => (
            <button key={i} data-testid={`brain-option-${i}`} onClick={() => setChoice(i)}
              className={`w-full text-left px-4 py-3 border text-sm transition-all ${choice === i ? "border-cyan-400 bg-cyan-400/10 text-cyan-200" : "border-pb-border text-zinc-300 hover:border-zinc-500"}`}>
              <span className="font-black text-cyan-400 mr-2">{String.fromCharCode(65 + i)}.</span>{o}
            </button>
          ))}
        </div>
      ) : (
        <textarea data-testid="brain-written-input" value={text} onChange={(e) => setText(e.target.value)} rows={4}
          placeholder="Read the court. What is your decision — and why?"
          className="w-full bg-pb-elevated border border-pb-border focus:border-cyan-400 outline-none p-3 text-sm text-zinc-200 mb-5" />
      )}
      <button data-testid="brain-submit-btn" disabled={busy || (q.mode === "choice" ? choice == null : !text.trim())} onClick={submit}
        className="flex items-center gap-2 bg-cyan-400 text-black px-6 py-3 text-[11px] font-black uppercase tracking-widest hover:bg-cyan-300 disabled:opacity-40 transition-colors">
        {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> AI evaluating your decision…</> : <>Lock In Decision <Target className="w-4 h-4" /></>}
      </button>
    </div>
  );
}

export default function BrainTraining() {
  const navigate = useNavigate();
  const [st, setSt] = useState(null);
  const [q, setQ] = useState(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [hist, setHist] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/brain/status"); setSt(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const start = async (params = "") => {
    setBusy(true);
    try {
      const { data } = await api.post(`/brain/next${params}`);
      setQ(data.question);
      if (data.coins_charged > 0) toast.success(`Extra scenario unlocked — ${data.coins_charged} BLC`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (st?.has_open_session && !q) start(); }, [st]); // eslint-disable-line

  const getReport = async () => {
    setBusy(true);
    try { const { data } = await api.get("/brain/report"); setReport(data.report); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const getHist = async () => {
    try { const { data } = await api.get("/brain/history"); setHist(data.attempts); } catch {}
  };

  const quota = st?.quota;
  const earned = new Set((st?.badges || []).map((b) => b.id));

  return (
    <div data-testid="brain-training-page" className="relative">
      <MatrixRain />
      <div className="relative z-[1]">
        <PageHeader kicker="PBPA · Court Mentality Training Hub"
          title={<>Brain Training <Brain className="inline w-9 h-9 sm:w-11 sm:h-11 text-cyan-300 align-baseline ml-1 drop-shadow-[0_0_10px_rgba(34,211,238,0.65)]" /></>}
          subtitle="Not trivia. Real court situations, real decisions — evaluated by AI across 10 tactical dimensions. Think better, play better." />

        {q ? (
          <QuestionCard q={q} onDone={() => load()} onExit={{ next: () => { setQ(null); start(); }, dash: () => setQ(null) }} />
        ) : !st ? (
          <div className="flex items-center gap-2 text-zinc-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading your brain profile…</div>
        ) : (
          <>
            {/* Action row */}
            <div className="grid sm:grid-cols-3 gap-4 mb-8">
              <button data-testid="start-training-btn" disabled={busy || quota.remaining <= 0} onClick={() => start()}
                className="group border border-cyan-400/60 bg-cyan-400/10 p-5 text-left hover:bg-cyan-400 hover:text-black transition-all disabled:opacity-40">
                <Swords className="w-6 h-6 text-cyan-300 group-hover:text-black mb-2" />
                <div className="font-display font-black uppercase text-lg leading-none">Start Training</div>
                <div className="text-[10px] uppercase tracking-[0.2em] mt-1 opacity-70">{quota.unlimited ? "Unlimited scenarios" : `${quota.remaining} left this ${quota.period}`}</div>
              </button>
              <button data-testid="daily-challenge-btn" disabled={busy || !st.daily_challenge || st.daily_challenge.completed || quota.remaining <= 0}
                onClick={() => start("?daily=true")}
                className="group border border-amber-400/60 bg-amber-400/10 p-5 text-left hover:bg-amber-400 hover:text-black transition-all disabled:opacity-40">
                <Sparkles className="w-6 h-6 text-amber-400 group-hover:text-black mb-2" />
                <div className="font-display font-black uppercase text-lg leading-none">Today's Challenge {st.daily_challenge?.completed && "✓"}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] mt-1 opacity-70">
                  {st.daily_challenge ? `${st.daily_challenge.category} · ${st.daily_challenge.level_name} · 2× XP` : "VIP feature — upgrade to unlock"}
                </div>
              </button>
              <button data-testid="attack-weakness-btn" disabled={busy || !quota.adaptive || !st.weakness || quota.remaining <= 0}
                onClick={() => start("?focus=true")}
                className="group border border-red-400/60 bg-red-400/10 p-5 text-left hover:bg-red-400 hover:text-black transition-all disabled:opacity-40">
                <Target className="w-6 h-6 text-red-400 group-hover:text-black mb-2" />
                <div className="font-display font-black uppercase text-lg leading-none">Attack My Weakness</div>
                <div className="text-[10px] uppercase tracking-[0.2em] mt-1 opacity-70">
                  {!quota.adaptive ? "ACE feature — adaptive retesting" : st.weakness ? `Retest: ${st.weakness}` : "Complete 5+ scenarios per category"}
                </div>
              </button>
            </div>
            {quota.remaining <= 0 && !quota.unlimited && (
              <div data-testid="brain-quota-wall" className="border border-amber-400/50 bg-amber-400/5 p-5 mb-8 flex flex-wrap items-center gap-4">
                <Lock className="w-6 h-6 text-amber-400" />
                <div className="flex-1 min-w-[220px]">
                  <div className="font-display font-black uppercase">You've used all {quota.limit} scenarios this {quota.period}</div>
                  <p className="text-xs text-zinc-400 mt-1">VIP unlocks 10/day + the Daily Challenge. ACE unlocks unlimited scenarios, Levels 1–5, adaptive weakness training and weekly tactical reports.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {quota.extra_cost > 0 && (
                    <button data-testid="buy-extra-scenario-btn" disabled={busy} onClick={() => start("?paid=true")}
                      className="border border-amber-400 text-amber-400 hover:bg-amber-400 hover:text-black px-5 py-2.5 text-[11px] font-black uppercase tracking-widest transition-colors disabled:opacity-50">
                      Buy Extra Scenario · {quota.extra_cost} BLC
                    </button>
                  )}
                  <button onClick={() => navigate("/upgrade")} className="bg-amber-400 text-black px-5 py-2.5 text-[11px] font-black uppercase tracking-widest">Upgrade</button>
                </div>
              </div>
            )}

            {/* MY BRAIN */}
            <div className="grid lg:grid-cols-3 gap-6 mb-8">
              <div className="bg-pb-surface border border-pb-border p-6" data-testid="my-brain-card">
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3 flex items-center gap-2"><Brain className="w-4 h-4 text-cyan-300" /> My Brain</div>
                <div className="flex items-end gap-3 mb-1">
                  <div className="font-display font-black text-7xl leading-none" style={{ color: st.iq == null ? "#52525B" : bandColor(st.iq) }}>{st.iq == null ? "—" : Math.round(st.iq)}</div>
                  <div className="pb-1">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Overall Brain IQ</div>
                    <div className="font-black uppercase" style={{ color: st.iq == null ? "#52525B" : bandColor(st.iq) }}>{st.iq_band || "Unrated"}</div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 mt-4 mb-5">
                  <div><div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500">Streak</div><div className="font-display font-black text-xl text-orange-400 flex items-center gap-1"><Flame className="w-4 h-4" />{st.streak}d</div></div>
                  <div><div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500">IQ XP</div><div className="font-display font-black text-xl text-amber-400 flex items-center gap-1"><Zap className="w-4 h-4" />{st.xp}</div></div>
                  <div><div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500">Scenarios</div><div className="font-display font-black text-xl text-zinc-200">{st.attempts}</div></div>
                </div>
                <div className="space-y-2.5">
                  {Object.entries(st.facets).map(([k, v]) => <IQBar key={k} label={k} value={v} testid={`facet-${k.replace(/\s/g, "-").toLowerCase()}`} />)}
                </div>
                <div className="mt-5 grid grid-cols-2 gap-3">
                  <div className="border border-lime-400/30 bg-lime-400/5 p-3">
                    <div className="text-[9px] uppercase tracking-[0.2em] text-lime-400">Current Strength</div>
                    <div className="font-bold text-sm mt-0.5">{st.strength || "Keep training"}</div>
                  </div>
                  <div className="border border-red-400/30 bg-red-400/5 p-3">
                    <div className="text-[9px] uppercase tracking-[0.2em] text-red-400">Current Weakness</div>
                    <div className="font-bold text-sm mt-0.5">{st.weakness || "Not enough data"}</div>
                  </div>
                </div>
                {st.recommended_drill && (
                  <div className="mt-3 border border-amber-400/30 bg-amber-400/5 p-3 flex items-center gap-3">
                    <Dumbbell className="w-4 h-4 text-amber-400 shrink-0" />
                    <div className="flex-1">
                      <div className="text-[9px] uppercase tracking-[0.2em] text-amber-400">Recommended Drill</div>
                      <div className="text-sm font-bold">{st.recommended_drill.name} · {st.recommended_drill.level}</div>
                    </div>
                    <button onClick={() => navigate("/training")} className="text-[9px] font-black uppercase tracking-widest border border-amber-400 text-amber-400 hover:bg-amber-400 hover:text-black px-3 py-1.5 transition-colors">Go</button>
                  </div>
                )}
              </div>

              {/* Category grid */}
              <div className="bg-pb-surface border border-pb-border p-6" data-testid="brain-categories-card">
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4">Category IQ · 15 Tactical Domains</div>
                {Object.keys(st.categories).length === 0 ? (
                  <p className="text-sm text-zinc-500">Answer scenarios to start mapping your tactical brain. Each category needs 5 reps before it counts as a verified strength or weakness.</p>
                ) : (
                  <div className="space-y-2.5">
                    {Object.entries(st.categories).sort((a, b) => b[1].score - a[1].score).map(([c, v]) => (
                      <div key={c} className="flex items-center gap-2">
                        <div className="text-[10px] uppercase tracking-wider text-zinc-400 w-40 shrink-0 truncate">{c} <span className="text-zinc-600">×{v.n}</span></div>
                        <div className="flex-1 h-1.5 bg-pb-elevated"><div className="h-full" style={{ width: `${v.score}%`, background: bandColor(v.score) }} /></div>
                        <div className="text-[10px] font-bold w-8 text-right" style={{ color: bandColor(v.score) }}>{Math.round(v.score)}</div>
                      </div>
                    ))}
                  </div>
                )}
                <button onClick={() => (hist ? setHist(null) : getHist())} data-testid="brain-history-toggle"
                  className="mt-5 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-zinc-500 hover:text-cyan-300 transition-colors"><History className="w-3.5 h-3.5" /> {hist ? "Hide" : "Show"} recent scenarios</button>
                {hist && (
                  <div className="mt-3 space-y-1.5 max-h-56 overflow-y-auto">
                    {hist.map((h, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs border-b border-pb-border/50 pb-1.5">
                        <span className="text-zinc-600">L{h.level}</span><span className="flex-1 text-zinc-300 truncate">{h.title}</span>
                        <span className="font-bold" style={{ color: bandColor(h.score) }}>{Math.round(h.score)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Badges + report */}
              <div className="bg-pb-surface border border-pb-border p-6" data-testid="brain-badges-card">
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4 flex items-center gap-2"><Award className="w-4 h-4 text-amber-400" /> IQ Badges</div>
                <div className="grid grid-cols-2 gap-2 mb-5">
                  {st.all_badges.map((b) => (
                    <div key={b.id} title={b.desc}
                      className={`border p-2.5 ${earned.has(b.id) ? "border-amber-400/60 bg-amber-400/10 text-amber-300" : "border-pb-border text-zinc-600"}`}>
                      <div className="text-[10px] font-black uppercase tracking-wider">{b.name}</div>
                      <div className="text-[9px] mt-0.5 opacity-70">{b.desc}</div>
                    </div>
                  ))}
                </div>
                <button data-testid="weekly-report-btn" onClick={getReport} disabled={busy || !quota.adaptive}
                  className="w-full flex items-center justify-center gap-2 border border-cyan-400/60 text-cyan-300 hover:bg-cyan-400 hover:text-black px-4 py-2.5 text-[10px] font-black uppercase tracking-widest transition-colors disabled:opacity-40">
                  <FileText className="w-4 h-4" /> {quota.adaptive ? "Weekly Tactical Report" : "Weekly Report — ACE only"}
                </button>
                {report && <div className="mt-4 border border-cyan-400/30 bg-cyan-400/5 p-4 text-xs text-zinc-300 whitespace-pre-wrap" data-testid="weekly-report-text">{report}</div>}
              </div>
            </div>

            <button onClick={() => navigate("/profile")} className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-zinc-500 hover:text-cyan-300 transition-colors mb-4">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to Player Profile
            </button>
          </>
        )}
      </div>
    </div>
  );
}
